// utils.ts太大了，题目相关的工具函数，尽量写到这个文件中
import lottie from 'lottie-web';
import {initPIXIMock} from '@/utils/pixiMock';
import {
    consoleLog, parseUrl, sendMessageApp,
    isGaotuEnv, isWebEnv, isLowEndDevice, startCheckForStutter
} from './utils';

/**
 * 异常上报
 * exceptionErrorcode 取值范围：
 * - 100001: 请求题目数据失败，extra: {questionId, randomSign}
 *   - errorCode: -1 首次请求超时
 *   - errorCode: -2 重试后超时
 *   - errorCode: -3 重试后失败（非超时）
 *   - errorCode: -4 首次请求失败（非超时）
 * - 100002: 选项/题干语音播放失败（超时兜底时errorCode为-22307）
 * - 100003: 选项/题干图片加载失败，extra: {imageUrl: xxxxx, isLocal: 0} // 0-非本地，1-本地
 * - 100004: spine动效加载失败，extra: {url: xxxxx}
 *   - errorCode: -32307 加载超时
 *   - errorCode: 空字符串 加载失败
 * - 101001: 语音评测结束无回调，走超时兜底结束
 */
interface QuestionErrorReportParams {

    /** 错误事件类型 */
    exceptionErrorcode: number;

    /** 错误码 */
    errorCode?: string;

    /** 报错信息 */
    message?: string;

    /** 额外信息，json字符串 */
    extra?: string;
}
export const questionErrorReport = (params: QuestionErrorReportParams) => {
    const reportData = {
        exceptionErrorcode: params.exceptionErrorcode,
        errorCode: params.errorCode ?? '',
        message: params.message ?? '',
        extra: params.extra ?? '{}',
    };
    sendMessageApp('questionErrorReport', reportData);
};

/**
 * 语音评测超时兜底上报
 */
export const reportSpeechEvaluateTimeout = () => {
    questionErrorReport({
        exceptionErrorcode: 101001,
        errorCode: '',
        message: '语音评测结束无回调，走超时兜底结束',
    });
};

/**
 * 创建模拟的 lottie animation 对象
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createMockAnimation = (config: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventListeners: {[key: string]: ((...args: any[]) => void)[]} = {};
    const immediateEvents = ['DOMLoaded', 'data_ready'];

    // 触发立即事件的辅助函数
    const triggerImmediateEvent = (eventName: string) => {
        const listeners = eventListeners[eventName] || [];
        listeners.forEach(listener => {
            try {
                listener();
            }
            catch (error) {
                console.log(`mock animation ${eventName} listener error`, error);
            }
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mockAnimation: any = {
        // 基本属性
        loop: false,
        autoplay: false,
        renderer: config?.renderer || 'svg',
        container: config?.container || null,
        animationData: config?.animationData || null,
        animationType: config?.animationType || 0,
        assetsPath: config?.assetsPath || '',

        // 控制方法
        play() {
            // 不执行实际播放
        },
        pause() {
            // 不执行实际暂停
        },
        stop() {
            // 不执行实际停止
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setSpeed(_speed: number) {
            // 不执行实际设置速度
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setDirection(_direction: number) {
            // 不执行实际设置方向
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        goToAndStop(_value: number, _isFrame?: boolean) {
            // 不执行实际跳转
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        goToAndPlay(_value: number, _isFrame?: boolean) {
            // 不执行实际跳转
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setSubframe(_flag: boolean) {
            // 不执行实际设置
        },
        resize() {
            // 不执行实际调整大小
        },
        destroy() {
            // 清理事件监听器
            Object.keys(eventListeners).forEach(key => {
                eventListeners[key] = [];
            });
        },

        // 事件监听
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        addEventListener(eventName: string, listener: (...args: any[]) => void) {
            if (!eventListeners[eventName]) {
                eventListeners[eventName] = [];
            }
            eventListeners[eventName].push(listener);

            // 如果是立即事件（DOMLoaded 或 data_ready），注册后立即触发
            if (immediateEvents.includes(eventName)) {
                setTimeout(() => {
                    triggerImmediateEvent(eventName);
                }, 0);
            }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        removeEventListener(eventName: string, listener: (...args: any[]) => void) {
            if (eventListeners[eventName]) {
                const index = eventListeners[eventName].indexOf(listener);
                if (index > -1) {
                    eventListeners[eventName].splice(index, 1);
                }
            }
        },

        // 获取状态（返回模拟值）
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        getDuration(_inFrames?: boolean) {
            return 0;
        },
        isLoaded() {
            return true;
        },
        isPaused() {
            return false;
        },
    };

    return mockAnimation;
};

/**
 * 初始化高途环境下的动效 Mock（lottie 和 PIXI）
 * 在高途直播间环境中，重写 lottie.loadAnimation 方法和 PIXI
 */
export const initGaotuAnimationMock = () => {
    if (!isGaotuEnv() || isWebEnv() || location.host === 'test-interactive.gaotu.cn') {
        return;
    }

    // 初始化PIXI Mock（在PIXI脚本加载后执行）
    if (typeof window !== 'undefined') {
        const initMock = () => {
            if (window.PIXI) {
                initPIXIMock(true);
            }
            else {
                // 如果PIXI还没加载，等待一下再试
                setTimeout(initMock, 100);
            }
        };
        // 如果PIXI已经存在，立即初始化；否则等待
        if (window.PIXI) {
            initPIXIMock(true);
        }
        else if (document.readyState === 'loading') {
            // 监听DOMContentLoaded或延迟执行
            document.addEventListener('DOMContentLoaded', initMock);
        }
        else {
            setTimeout(initMock, 0);
        }
    }

    // 重写 lottie.loadAnimation 方法
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lottie.loadAnimation = (config: any) => {
        console.log('lottie.loadAnimation mocked for gaotu env', config);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return createMockAnimation(config) as any;
    };
};

/**
 * 图片加载监控
 * 仅在非高途环境的题目中调用
 */
export const initImageMonitor = () => {
    // 高途环境不启用图片监控
    if (isGaotuEnv()) {
        return;
    }

    // 已上报的图片URL集合（用于去重）
    const reportedImages = new Set<string>();

    // 判断是否为本地资源
    function isLocalResource(url: string): boolean {
        return url.startsWith('file://') || url.startsWith('/') || url.startsWith('data:') || url.includes('localhost');
    }

    // 统一的日志输出
    function logImageLoad(src: string, status: 'success' | 'error' | 'cached', loadTime: number, details?: {
        dns?: number;
        tcp?: number;
        response?: number;
    }) {
        // 去重：同一URL只上报一次
        if (reportedImages.has(src)) {
            return;
        }
        reportedImages.add(src);

        const statusText = status === 'success' ? '加载成功' : status === 'error' ? '加载失败' : '命中缓存';
        let message = `[图片监控] ${statusText} | 耗时: ${loadTime.toFixed(2)}ms | ${src}`;
        if (details) {
            const dnsTime = details.dns?.toFixed(2) ?? '0';
            const tcpTime = details.tcp?.toFixed(2) ?? '0';
            const responseTime = details.response?.toFixed(2) ?? '0';
            message += ` | DNS: ${dnsTime}ms, TCP: ${tcpTime}ms, 响应: ${responseTime}ms`;
        }
        console.log(message);

        if (src.endsWith('.html')) {
            return;
        }

        // 火山上报图片加载耗时
        window.collectEvent?.('yd_image_load_report', {
            imageUrl: src,
            status,
            loadTime: Math.round(loadTime),
            isLocal: isLocalResource(src) ? 1 : 0,
            dnsTime: details?.dns ? Math.round(details.dns) : 0,
            tcpTime: details?.tcp ? Math.round(details.tcp) : 0,
            responseTime: details?.response ? Math.round(details.response) : 0,
        });

        // 图片加载失败时进行端能力上报
        if (status === 'error') {
            questionErrorReport({
                exceptionErrorcode: 100003,
                errorCode: '',
                message: '图片加载失败',
                extra: JSON.stringify({
                    imageUrl: src,
                    isLocal: isLocalResource(src) ? 1 : 0,
                }),
            });
        }
    }

    // 给单个img元素绑定加载监控
    function trackImageLoadTime(img: HTMLImageElement) {
        if (img.complete) {
            logImageLoad(img.src, 'cached', 0);
            return;
        }

        const startTime = performance.now();

        // 使用 once: true 自动移除监听器
        img.addEventListener('load', () => {
            const loadTime = performance.now() - startTime;
            logImageLoad(img.src, 'success', loadTime);
        }, {once: true});

        img.addEventListener('error', () => {
            const loadTime = performance.now() - startTime;
            logImageLoad(img.src, 'error', loadTime);
        }, {once: true});
    }

    // 1. 监控页面初始加载的图片（使用 Performance API）
    window.addEventListener('load', () => {
        const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        entries
            .filter(entry => entry.initiatorType === 'img')
            .forEach(entry => {
                logImageLoad(entry.name, 'success', entry.responseEnd - entry.startTime, {
                    dns: entry.domainLookupEnd - entry.domainLookupStart,
                    tcp: entry.connectEnd - entry.connectStart,
                    response: entry.responseEnd - entry.requestStart
                });
            });
    });

    // 处理新增节点中的图片
    function handleAddedNode(node: Node) {
        const element = node as HTMLElement;
        if (element.tagName === 'IMG') {
            trackImageLoadTime(element as HTMLImageElement);
        }
        else if (element.nodeType === 1 && element.querySelectorAll) {
            element.querySelectorAll('img').forEach((img: HTMLImageElement) => {
                trackImageLoadTime(img);
            });
        }
    }

    // 2. 监控动态插入的图片（使用 MutationObserver）
    window.addEventListener('DOMContentLoaded', () => {
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(handleAddedNode);
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    });
};

interface FileListItem {
    uuid: string;
    original: string;
    local: string;
}

const handleClientQuestionItem = (item: any, fileMap: Map<string, FileListItem>) => {
    // 获取item的type，只处理对象和数组
    const itemType = Object.prototype.toString.call(item);
    // consoleLog('handleQuestionItem', itemType, item);
    if (itemType === '[object Object]') {
        // 处理对象
        for (const key of Object.keys(item)) {
            const value = item[key];
            const innerType = Object.prototype.toString.call(value);
            if (innerType === '[object Object]') {
                handleClientQuestionItem(value, fileMap);
            }
            else if (innerType === '[object Array]') {
                value.forEach((value: any) => handleClientQuestionItem(value, fileMap));
            }
            else if (innerType === '[object String]' && fileMap.has(value)) {
                const fileListItem = fileMap.get(value)!;
                const original = fileListItem.original || '';
                const local = fileListItem.local || '';
                consoleLog('命中本地资源 uuid', value);
                consoleLog('命中本地资源 original', original);
                consoleLog('命中本地资源 local', local);
                if (original.includes('|') || !original) {
                    item[key] = original;
                }
                else {
                    item[key] = local;
                }
            }
        }
    }
    else if (itemType === '[object Array]') {
        // 处理数组
        item.forEach((value: any) => handleClientQuestionItem(value, fileMap));
    }
};

// questionData是题目数据的
const handleClientQuestionData = (questionDataStr: string, fileList: any[]): any => {
    const questionData = JSON.parse(questionDataStr);
    consoleLog('questionData', questionData);
    const fileMap: Map<string, FileListItem> = new Map(
        fileList.map((item: FileListItem) => [item.uuid, item])
    );

    handleClientQuestionItem(questionData.qBody, fileMap);
    handleClientQuestionItem(questionData.qSelection, fileMap);

    return questionData;
};

export const sendMessageAppInOnlineWithoutFetch = async (fnName: string) => {
    if (fnName !== 'loadFinish') {
        return;
    }

    const {questionId} = parseUrl();
    consoleLog(`无需调用接口的 questionId ${questionId}`);

    const newParams = {
        params: '',
        uid: '',
        cTableId: '',
        courseId: 0,
        sessionId: '',
        questionId,
        roomId: '',
        preHref: '',
        teacherCharacter: '1',
        isLowEndDevice: isLowEndDevice(),
        fileList: [] as any[],
    };

    // 等待端上参数的 Promise
    const question = await new Promise<any>(resolve => {
        window.callbackMap.setQuestionParams = (json: any) => {
            const {
                params, uid, cTableId, courseId,
                sessionId, roomId, preHref, teacherCharacter,
                fileList, questionData
            } = JSON.parse(json);
            Object.assign(newParams, {
                uid: uid || '',
                cTableId: cTableId || '',
                courseId: courseId || 0,
                sessionId: sessionId || '',
                roomId: roomId || '',
                preHref: preHref || '',
                teacherCharacter: String(teacherCharacter) || '1',
                fileList: fileList || [],
            });
            consoleLog('客户端参数已接收', newParams);
            // consoleLog('客户端传递的题目数据', questionData);

            const question = handleClientQuestionData(questionData, fileList || []);
            newParams.params = question;

            resolve(question);
        };
    });

    window.callbackMap.setNewQuestionParams(JSON.stringify(newParams));


    if (!isWebEnv()) {
        startCheckForStutter({
            qTypeId: question.qTypeId || -1,
            qName: question.qname || '',
            questionId: question.questionId || -1,
        });
    }
};


let startTime = Date.now();
let clickLatency = -1;
let startSilentWait = -1;
let clickSign = false;
let speechSign = false;
const handleQuestionStart = () => {
    startTime = Date.now();
};
const handleFirstTouchInteraction = () => {
    if (clickSign) {
        return;
    }
    clickSign = true;
    const curTime = Date.now();
    // clickLatency = Math.round((curTime - startTime) / 1000);
    clickLatency = curTime - startTime;
};
export const handleSpeechCallback = (overall: number) => {
    if (speechSign || overall < 10) {
        return;
    }
    speechSign = true;
    const curTime = Date.now();
    // startSilentWait = Math.round((curTime - startTime) / 1000);
    startSilentWait = curTime - startTime;
};
const handleStepQuestion = (param: any) => {
    if (!param) {
        return param;
    }
    try {
        const data = typeof param === 'string' ? JSON.parse(param) : param;
        const contentStr = data.content || '{}';
        const content = JSON.parse(contentStr);
        content.clickLatency = clickLatency;
        content.startSilentWait = startSilentWait;
        data.content = JSON.stringify(content);
        return JSON.stringify(data);
    }
    catch (error) {
        return param;
    }
};

export const handleSendMessageApp = (fnName: string, param?:any):void => {
    if (fnName === 'startQuestion') {
        handleQuestionStart();
    }
    else if (fnName === 'firstTouchInteraction') {
        handleFirstTouchInteraction();
    }
    else if (fnName === 'finishedQuestion' || fnName === 'updateQuestionScore') {
        return handleStepQuestion(param);
    }
    return param;
};
